import { useContext, useEffect, useState } from "react";
import { Button, Col, Row, Tabs } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import TrialModal from "../common/TrialModal";
import ExecutiveLeft from "../components/executive-search/ExecutiveLeft";
import ExecutiveContent from "../components/executive-search/ExecutiveContent";
import ExecutiveNavigation from "../components/executive-search/ExecutiveNavigation";
import "../assets/css/dynemic-page.css";
import {
  getExecutiveEmployeeList,
  resetQuickSelectedRow,
  saveAdvancedSelectedFilters,
  selectedRow,
} from "../actionCreator/executiveListingActionCreater";
import { AuthContext } from "../context/AuthContext";
import CompanyLogo from "../common/CompanyLogo";
import UserLogo from "../common/UserLogo";
import { Users } from "lucide-react";
import bgImg from "../assets/images/executive-bg.jpg";
import subImg from "../assets/images/executive-modal-logo.jpg";

const ExecutiveSearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("2");
  const { isAuthenticated, collapsed } = useContext(AuthContext);
  const { isExecutiveView, isLeadView, isCompanyView } =
    useContext(AuthContext);
  const topSearchValue = useSelector(
    (state) => state?.HeaderReducer?.topSearchValue,
  );
  const topSearchClick = useSelector(
    (state) => state.HeaderReducer.topSearchClick,
  );

  useEffect(() => {
    if (!isExecutiveView && isAuthenticated) {
      navigate("/dashboard-view");
    }
  }, [isExecutiveView, navigate, isAuthenticated]);

  useEffect(() => {
    const getExecutives = () => {
      const allParams = {};
      for (const [key, value] of searchParams.entries()) {
        allParams[key] = value;
      }

      dispatch(
        getExecutiveEmployeeList({
          ...allParams,
          Name: topSearchValue,
          page: allParams?.page ? allParams?.page - 1 : 0,
        }),
      );
    };
    if (isAuthenticated) getExecutives();
  }, [dispatch, searchParams, isAuthenticated, topSearchValue, topSearchClick]);

  const onChange = (key) => {
    setActiveTab(key);
    dispatch(selectedRow([]));
    if (key === "1") {
      navigate("/search-company");
    }
    if (isAuthenticated && key === "3") {
      navigate("/search-lead");
    }
    dispatch(resetQuickSelectedRow([]));
    dispatch(
      saveAdvancedSelectedFilters({
        quickSelectionRange: null,
      }),
    );
  };

  const redirectToSignup = () => {
    navigate("/signup");
  };

  const items = [
    {
      key: "1",
      isVisible: isAuthenticated ? isCompanyView : true,
      label: (
        <span className="d-flex align-items-center gap-10">
          <CompanyLogo />
          Companies
        </span>
      ),
    },
    {
      key: "2",
      isVisible: isAuthenticated ? isExecutiveView : true,
      label: (
        <span className="d-flex align-items-center gap-10">
          <UserLogo />
          Executive
        </span>
      ),
      children: (
        <>
          {isAuthenticated ? (
            <Row id="wrapper" className="tw-mt-2">
              <Col md={6} xl={5} xxl={4}>
                <ExecutiveLeft />
              </Col>
              <Col md={18} xl={19} xxl={20} className="tw-p-0">
                <ExecutiveContent />
              </Col>
            </Row>
          ) : (
            <>
              <img src={bgImg} alt="" />
              <div
                style={{
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  background: "white",
                  width: "380px",
                  borderRadius: "12px",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                  overflow: "hidden",
                  zIndex: 9999,
                }}
              >
                <img
                  src={subImg}
                  alt="cover"
                  style={{
                    width: "100%",
                    height: "160px",
                    objectFit: "cover",
                  }}
                />

                <div className="p-20">
                  <h3 className="heading18px mb10 fw-semibold">
                    Almost There!
                  </h3>

                  <p className="text-color-848484 mb20">
                    Your curated CXO & decision-maker leads are right behind
                    this screen. Subscribe now to instantly unlock the full
                    executive list.
                  </p>

                  <Button
                    type="primary"
                    block
                    className="theme-btn"
                    onClick={() => navigate("/signup")}
                  >
                    Subscribe to View
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      ),
    },
    {
      key: "3",
      isVisible: isAuthenticated ? isLeadView : true,
      label: (
        <span className="d-flex align-items-center gap-10">
          <Users size={18} />
          Leads
        </span>
      ),
    },
  ];

  const companySelectedFilterList = useSelector(
    (state) => state.executiveListingReducer.selectedFilters,
  );
  const selectedRecords = useSelector(
    (state) => state.executiveListingReducer.selectedRecords,
  );
  const companyFilterList = useSelector(
    (state) => state.executiveListingReducer,
  );

  const currentPage = parseInt(searchParams.get("page")) || 1;
  const itemsPerPage = parseInt(searchParams.get("size")) || 10;
  const totalItems = companyFilterList?.totalExecutiveCount;
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div
      id="tabscompany"
      className={
        collapsed
          ? "tabs-extra-content-collapsed tw-px-4 tw-py-4 tw-h-[calc(100vh-64px)] tw-overflow-hidden"
          : "tabs-extra-content tw-px-4 tw-py-4 tw-h-[calc(100vh-64px)] tw-overflow-hidden"
      }
    >
      <Row className="tw-items-center tw-mb-2 tw-gap-y-2">
        <Col xs={24} md={10} lg={12}>
          <h2 className="tw-text-2xl tw-font-bold tw-text-slate-800 tw-m-0">
            Data Search
          </h2>
          <p className="tw-text-slate-500 tw-text-xs tw-mt-1 tw-mb-0">
            Search and filter lead accounts, organizations, and industry-wide company datasets.
          </p>
        </Col>
        <Col
          xs={24}
          md={14}
          lg={12}
          className="tw-flex tw-items-center md:tw-justify-end tw-gap-2 tw-flex-wrap"
        >
          {companySelectedFilterList?.quickSelectionRange && (
            <span className="tw-bg-slate-100 tw-text-slate-600 tw-text-xs tw-font-medium tw-py-1 tw-px-3 tw-rounded-full">
              Quick Pages: {companySelectedFilterList?.quickSelectionRange?.start} - {companySelectedFilterList?.quickSelectionRange?.end}
            </span>
          )}
          {!!selectedRecords?.length && (
            <span className="tw-bg-[#EFEAFF] tw-text-[#5D44FF] tw-text-xs tw-font-semibold tw-py-1 tw-px-3 tw-rounded-full tw-shadow-sm">
              {selectedRecords?.length} records selected
            </span>
          )}
          {totalItems > 0 && (
            <span className="tw-bg-slate-100 tw-text-slate-600 tw-text-xs tw-font-medium tw-py-1 tw-px-3 tw-rounded-full">
              Showing {startItem} - {endItem} of{" "}
              {companyFilterList?.totalExecutiveCount?.toLocaleString() || totalItems} results
            </span>
          )}
        </Col>
      </Row>

      <Tabs
        className="tw-font-inter"
        tabBarExtraContent={{
          right: isAuthenticated ? <ExecutiveNavigation /> : <></>,
        }}
        activeKey={activeTab}
        items={
          isAuthenticated
            ? items?.filter((d) => d?.isVisible).slice(0, 2)
            : items?.filter((d) => d?.isVisible)
        }
        onChange={onChange}
      />
      <TrialModal
        openModal={!isAuthenticated && activeTab === "3"}
        closeModal={redirectToSignup}
        redirectToSignup={redirectToSignup}
        redirect={true}
        isSubscriptionMissing
      />
    </div>
  );
};

export default ExecutiveSearch;
